import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { DeviceVerificationLauncher } from "~/components/DeviceVerificationLauncher";
import { GhostButton, PrimaryButton } from "~/components/MessageButtons";
import { PresenceIndicator } from "~/components/PresenceIndicator";
import { DndCrescent } from "~/components/StatusGlyph";
import { useMatrix } from "~/context/MatrixContext";
import { useAuthenticatedMedia } from "~/hooks/useAuthenticatedMedia";
import { useDnd } from "~/hooks/useDnd";
import { useFocusTrap } from "~/hooks/useFocusTrap";
import { useUserProfile } from "~/hooks/useUserProfile";
import { getInitials } from "~/lib/initials";
import {
  getUserDevices,
  INVITE_NEEDS_VERIFIED_DEVICE,
  inviteUser,
  type Client,
} from "~/lib/matrix";
import { getPortalViewportSize, resolveAnchorPosition } from "~/lib/portal-positioning";
import { formatUntil } from "~/lib/relative-time";

interface UserProfileCardProps {
  userId: string;
  anchor: HTMLElement | null;
  onClose: () => void;
  roomId?: string | null;
  canInviteToRoom?: boolean;
  fallbackDisplayName?: string | null;
  fallbackAvatarUrl?: string | null;
}

const CARD_WIDTH = 320;
const OFFSET = 8;

function presenceLabel(state: "online" | "unavailable" | "offline"): string {
  if (state === "online") return "Online";
  if (state === "unavailable") return "Away";
  return "Offline";
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    ta.setAttribute("readonly", "");
    ta.setAttribute("aria-hidden", "true");
    document.body.appendChild(ta);
    try {
      ta.select();
      return document.execCommand("copy");
    } finally {
      document.body.removeChild(ta);
    }
  } catch {
    return false;
  }
}

export function UserProfileCard({
  userId,
  anchor,
  onClose,
  roomId,
  canInviteToRoom = false,
  fallbackDisplayName,
  fallbackAvatarUrl,
}: UserProfileCardProps) {
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { client, createDirectRoom, setActiveRoomId, userId: myUserId } = useMatrix();
  const profile = useUserProfile(client, userId, {
    displayName: fallbackDisplayName,
    avatarUrl: fallbackAvatarUrl,
  });
  const dnd = useDnd(client);
  const portalRoot = usePortalRoot();
  const [placement, setPlacement] = useState<{
    top: number;
    left: number;
    position: "fixed" | "absolute";
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState<"dm" | "invite" | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Stable handle that the ResizeObserver effect calls into. The
  // placement effect repoints `updateRef.current` on every run so the
  // observer always invokes the latest `update` closure (which
  // captures the current `anchor` / `portalRoot`) without forcing a
  // re-subscribe when the card's size changes.
  const updateRef = useRef<() => void>(() => {});
  // Gate the focus trap on `placement` rather than the card's mount —
  // we now return `null` until the positioning effect resolves the
  // first placement, so `cardRef.current` is `null` on the initial
  // render. Without this gate `useFocusTrap` would fire once with a
  // `null` ref, return early, and never re-arm (its deps are
  // `[ref, active]`), leaving the dialog without initial focus,
  // tab-cycling, or focus restoration. Toggling `active` from `false
  // → true` once placement resolves re-runs the effect at the moment
  // the card actually mounts.
  useFocusTrap(cardRef, placement !== null);

  // Placement runs under `useLayoutEffect` to match `MoreMenu`
  // (`MoreMenu.tsx:60`) and `PinnedPanel` (`PinnedPanel.tsx:43`):
  // both popovers commit their first measured position before paint
  // so the card never paints once at stale (or null) coords and then
  // jumps to its real spot on the next frame. The card's content can
  // grow after first measure (status text arrives, an error renders);
  // a `ResizeObserver` on the card recomputes when its rendered size
  // changes, so the placement no longer depends on every prop that
  // happens to feed the body — `error`, `profile.localTime`,
  // `profile.meetingUrl`, `profile.status`, `profile.statusMsg` —
  // tearing down the rAF / resize / scroll listeners on every state
  // churn. Layout, not content, is the signal.
  useLayoutEffect(() => {
    if (!anchor) return;
    const update = () => {
      const rect = anchor.getBoundingClientRect();
      // Surface dimensions come from `getPortalViewportSize` — the
      // viewport in standalone, the embed root's box when embedded —
      // so a card opened inside an embed clamps to that box rather
      // than spilling out into the host page. Same call-site shape
      // as `MoreMenu` / `PinnedPanel`.
      const surface = getPortalViewportSize(portalRoot);
      const surfaceBottom = surface.offsetTop + surface.height;
      const surfaceRight = surface.offsetLeft + surface.width;
      const cardHeight = cardRef.current?.offsetHeight ?? 240;
      const naturalTop = rect.bottom + OFFSET;
      const flipsUp = naturalTop + cardHeight > surfaceBottom;
      const viewportTop = flipsUp
        ? Math.max(surface.offsetTop + OFFSET, rect.top - cardHeight - OFFSET)
        : naturalTop;
      const maxLeft = surfaceRight - CARD_WIDTH - OFFSET;
      const viewportLeft = Math.max(surface.offsetLeft + OFFSET, Math.min(rect.left, maxLeft));
      setPlacement(resolveAnchorPosition({ portalRoot, viewportTop, viewportLeft }));
    };
    updateRef.current = update;
    update();
    const frame = window.requestAnimationFrame(update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchor, portalRoot]);

  // Recompute when the card's rendered size changes (status content
  // arrives, error toast renders, etc.) instead of listing every
  // content-bearing prop in the placement effect's dep array. Gate
  // on the *actual* render condition (`anchor`, `portalRoot`,
  // `placement`) — same predicate as the early-return guard before
  // `createPortal` below — so the observer is torn down and
  // re-attached when the card unmounts and remounts (e.g. anchor
  // toggles to null while open). Keying only on `placement` would
  // leave a stale `ResizeObserver` holding a detached DOM node if
  // `anchor` flipped null without `placement` resetting. `updateRef`
  // keeps the observer pointed at the latest `update` closure without
  // forcing a re-subscribe on each placement change.
  const isRendered = anchor !== null && portalRoot !== null && placement !== null;
  useLayoutEffect(() => {
    if (!isRendered) return;
    const card = cardRef.current;
    if (!card) return;
    const observer = new ResizeObserver(() => updateRef.current());
    observer.observe(card);
    return () => observer.disconnect();
  }, [isRendered]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (cardRef.current?.contains(target)) return;
      if (anchor?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [anchor, onClose]);

  const startDm = useCallback(async () => {
    setError(null);
    setBusy("dm");
    try {
      const id = await createDirectRoom({ userIds: [userId] });
      setActiveRoomId(id);
      onClose();
    } catch {
      setError("Could not open a DM. Try again.");
    } finally {
      setBusy(null);
    }
  }, [createDirectRoom, onClose, setActiveRoomId, userId]);

  const inviteToRoom = useCallback(async () => {
    if (!client || !roomId) return;
    setError(null);
    setBusy("invite");
    try {
      await inviteUser(client, roomId, userId);
      onClose();
    } catch (err) {
      if (err instanceof Error && err.message === INVITE_NEEDS_VERIFIED_DEVICE) {
        setError(INVITE_NEEDS_VERIFIED_DEVICE);
      } else {
        setError("Could not invite this member. Try again.");
      }
    } finally {
      setBusy(null);
    }
  }, [client, onClose, roomId, userId]);

  const copyMatrixId = useCallback(async () => {
    setError(null);
    const ok = await copyText(profile.matrixId);
    if (!ok) {
      setError("Could not copy Matrix ID. Try again.");
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [profile.matrixId]);

  if (!anchor || !portalRoot || !placement) return null;

  const canInvite = Boolean(canInviteToRoom && roomId && client && userId !== myUserId);
  return createPortal(
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={{
        position: placement.position,
        zIndex: 70,
        top: placement.top,
        left: placement.left,
        width: CARD_WIDTH,
        borderRadius: "var(--chat-radius-xl)",
        background: "var(--chat-surface-overlay)",
        boxShadow: "var(--chat-shadow-popover)",
        border: "1px solid var(--chat-surface-border)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        padding: "var(--chat-space-4)",
      }}
    >
      <div style={{ display: "flex", gap: "var(--chat-space-3)", minHeight: 72 }}>
        <UserProfileCardAvatar
          name={profile.displayName}
          avatarUrl={profile.avatarUrl}
          client={client}
          dndActive={userId === myUserId && dnd.active}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            id={titleId}
            style={{
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {profile.displayName}
          </div>
          <div
            style={{
              color: "var(--chat-fg-muted)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {profile.matrixId}
          </div>
        </div>
      </div>
      <div
        style={{ display: "grid", gap: "var(--chat-space-2)", marginTop: "var(--chat-space-3)" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "var(--chat-space-2)" }}>
          <PresenceIndicator state={profile.presence.state} surface="panel" />
          <span>{presenceLabel(profile.presence.state)}</span>
          {profile.status ? <span aria-hidden>{profile.status}</span> : null}
        </div>
        {profile.statusMsg ? <MutedLine>{profile.statusMsg}</MutedLine> : null}
        {userId === myUserId && dnd.active ? (
          <MutedLine>
            Do not disturb {dnd.until ? `— until ${formatUntil(dnd.until)}` : "— scheduled"}
          </MutedLine>
        ) : null}
        {profile.localTime && profile.timezone ? (
          <MutedLine>
            Local time {profile.localTime} ({profile.timezone})
          </MutedLine>
        ) : null}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--chat-space-2)",
          marginTop: "var(--chat-space-4)",
        }}
      >
        <PrimaryButton onClick={startDm} disabled={busy !== null || userId === myUserId}>
          Start DM
        </PrimaryButton>
        <GhostButton onClick={inviteToRoom} disabled={!canInvite || busy !== null}>
          Invite to room
        </GhostButton>
        {userId !== myUserId ? (
          <DeviceVerificationLauncher
            client={client}
            userId={userId}
            loadDevices={() => {
              if (!client) return Promise.resolve([]);
              return getUserDevices(client, userId);
            }}
            label="Verify session"
            variant="ghost"
          />
        ) : null}
        <GhostButton onClick={copyMatrixId}>{copied ? "Copied" : "Copy Matrix ID"}</GhostButton>
      </div>
      {error ? (
        <div
          role="alert"
          style={{
            marginTop: "var(--chat-space-3)",
            color: "var(--chat-state-danger)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
          }}
        >
          {error}
        </div>
      ) : null}
    </div>,
    portalRoot,
  );
}

// Exported for unit testing the placeholder → resolved transition
// (#3171). Production callers continue to use it through
// `<UserProfileCard>` only — the export is intentionally narrow and
// not re-exported through the package barrel.
export function UserProfileCardAvatar({
  name,
  avatarUrl,
  client,
  dndActive,
}: {
  name: string;
  avatarUrl: string | null;
  client: Client | null;
  dndActive: boolean;
}) {
  const common = {
    width: 48,
    height: 48,
    borderRadius: "var(--chat-radius-full)",
    flexShrink: 0,
  };
  // Resolve `mxc://` URIs through the authenticated SDK path — Synapse
  // ≥ 1.120 with `enable_authenticated_media: true` rejects the
  // unauthenticated v3 URL, and `<img src=…>` cannot supply the
  // `Authorization: Bearer` header the v1 path requires. Hook idles
  // while `client` or `avatarUrl` is null, so the initials fallback
  // covers the no-avatar case as well as the in-flight load.
  const avatarMxc = avatarUrl && avatarUrl.startsWith("mxc://") ? avatarUrl : null;
  const avatarMedia = useAuthenticatedMedia(client, avatarMxc);
  const resolvedHref = avatarMedia.src;
  if (resolvedHref) {
    return (
      <span style={{ position: "relative", flexShrink: 0 }}>
        <img src={resolvedHref} alt="" style={{ ...common, objectFit: "cover" }} />
        {dndActive ? <DndCrescent /> : null}
      </span>
    );
  }
  return (
    <span
      aria-hidden
      style={{
        ...common,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-secondary)",
        fontWeight: 500,
        fontSize: 20,
      }}
    >
      {getInitials(name)}
      {dndActive ? <DndCrescent /> : null}
    </span>
  );
}

function MutedLine({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        color: "var(--chat-fg-muted)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      {children}
    </div>
  );
}
